// Pure helpers shared by the two crew-profile editors: OfficerControls (on
// the public profile, editing someone else) and MyProfilePage (/cave/profile,
// editing yourself). Kept outside both components so "which controls show"
// and "what actually changed" can be unit tested without mounting anything.
import type { CrewMemberAdmin, Role, RosterSection } from '@clan-heart/shared';
import { roleSchema } from '@clan-heart/shared';

/** The 13 clan flags shipped under `/art/flags` — same fixed set RosterPage's
 * own picker offers, duplicated here since members and officers only ever
 * pick from this list (plus already-uploaded flags); neither may upload. */
const FLAG_NAMES = [
  'Thomas-Tew',
  'dutch-flag',
  'flag-hartje',
  'flag-heart-1',
  'flag-heart-standard-clean',
  'flag-heartlessqueen',
  'flag-hellheim',
  'flag-koldhorn',
  'flag-sambala',
  'flag-squid-davi-jon-pastel',
  'flag-viventor',
  'minori-flag',
  'sicklet',
];
export const CLAN_FLAGS: string[] = FLAG_NAMES.map((name) => `/art/flags/${name}.webp`);

export const SECTION_LABELS: Record<RosterSection, string> = {
  officers: 'Officers',
  members: 'Members',
  honorable: 'Honorable Mentions',
  dockmates: 'Respected Dockmates',
  feared: 'Feared Sailors',
};

export const ROLE_LABELS: Record<Role, string> = {
  owner: 'Owner',
  first_officer: 'First Officer',
  officer: 'Officer',
  member: 'Member',
};

/** A signed-in member may set up to this many characters of motto (matches
 * `motto` in shared/src/profiles.ts — the server is the source of truth; this
 * only drives the live counter). */
export const MOTTO_MAX = 160;

/**
 * Only the keys present in `edited` whose value actually differs from
 * `original` — the payload a PATCH should send. Comparison is by `!==`, so
 * callers must normalize (e.g. trim, '' -> null) before calling this.
 */
export function changedFields<T extends object>(original: T, edited: T): Partial<T> {
  const out: Partial<T> = {};
  for (const key of Object.keys(edited) as Array<keyof T>) {
    if (edited[key] !== original[key]) out[key] = edited[key];
  }
  return out;
}

/** What the "Officer controls" panel should show for a given admin record:
 * the roles it may offer to assign (exactly `canAssignRoles` — never
 * including the current role, which is shown separately) and whether the
 * Remove button appears at all. */
export interface OfficerPanelModel {
  currentRole: Role;
  roleOptions: Role[];
  showRemove: boolean;
}

/** roleSchema.options is ['owner', 'first_officer', 'officer', 'member'] —
 * reused here only to keep a stable, predictable order; see roleSchema. */
const ROLE_ORDER = roleSchema.options;

export function officerPanelModel(admin: Pick<CrewMemberAdmin, 'role' | 'canAssignRoles' | 'canRemove'>): OfficerPanelModel {
  const allowed = new Set(admin.canAssignRoles);
  return {
    currentRole: admin.role,
    roleOptions: ROLE_ORDER.filter((r) => allowed.has(r)),
    showRemove: admin.canRemove,
  };
}
